import { inject, Injectable, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { FirebaseService } from '@brightside-web/desktop/data-access/shared';
import { filter } from 'rxjs';

export enum MaintenanceProductType {
  External = 'external',
  Savings = 'savings',
  Spending = 'spending',
  Web = 'web',
  Contest = 'contest',
  Mpap = 'mpap',
  HelpingHandsConsents = 'helpinghandsconsents',
  HelpingHands = 'helpinghands',
  LoanDashboard = 'loandashboard',
  Loans = 'loans',
  Credit = 'credit',
  FindMoneyIntro = 'findmoneyintro',
  FindMoney = 'findmoney',
  TermsUpdate = 'termsupdate',
  AutoSave = 'autosave',
  Survey = 'survey',
  Chat = 'chat',
  ChatHistory = 'chathistory',
}

@Injectable({
  providedIn: 'root',
})
export class MaintenanceService {
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _analytics = inject(FirebaseService);
  private readonly _router = inject(Router);

  private _rules: { key: string; state: string }[];
  private _productMaintenanceMap: { [product: string]: boolean } = {};
  private _appInMaintenance = false;
  private _previousMaintenanceState = false;

  constructor() {
    this._analytics.ensureInitialized().then(() => {
      this.getRules();
    });
    
    // Wait for Firebase remote config to be ready, then check appMaintenance
    // This subscription will fire when remote config is initially ready and on subsequent updates
    this._analytics.remoteConfigReady$
      .pipe(
        filter((ready) => ready === true),
        takeUntilDestroyed(this._destroyRef)
      )
      .subscribe(() => {
        this.updateAppMaintenanceStatus();
      });
  }

  private updateAppMaintenanceStatus(): void {
    try {
      const newMaintenanceState = this._analytics.getValueAsBoolean('appMaintenance');
      
      // If maintenance mode just became active, redirect to maintenance page
      if (newMaintenanceState && !this._previousMaintenanceState) {
        const currentUrl = this._router.url;
        // Only redirect if we're not already on the maintenance page
        if (!currentUrl.startsWith('/maintenance')) {
          this._router.navigate(['/maintenance']);
        }
      }
      
      this._appInMaintenance = newMaintenanceState;
      this._previousMaintenanceState = newMaintenanceState;
    } catch (e) {
      // If key doesn't exist or there's an error, default to false
      this._appInMaintenance = false;
      this._previousMaintenanceState = false;
    }
  }

  private getRules() {
    try {
      this._rules = JSON.parse(this._analytics.getValueAsString('serviceInventoryOverride'));
      this.reduceRulesToMaintenanceMap();
    } catch (e) {
      setTimeout(() => {
        // keep trying until we get the rules
        this.getRules();
      }, 150);
    }
  }

  private reduceRulesToMaintenanceMap() {
    this._rules.forEach((rule) => {
      const { key, state } = rule;
      // Set to true if any rule for the product has inMaintenance: true
      if (state === 'MAINTENANCE') {
        this._productMaintenanceMap[key] = true;
      } else if (!(key in this._productMaintenanceMap)) {
        // Only set to false if no previous true flag was set
        this._productMaintenanceMap[key] = false;
      }
    });
  }

  public isProductInMaintenance(productType: string): boolean {
    return this._productMaintenanceMap[productType] || false;
  }

  public isAppInMaintenance(): boolean {
    return this._appInMaintenance;
  }
}
